import React, { useState } from 'react';
import { Outlet, NavLink, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../../context/AuthContext';
import { useNotifications } from '../../context/NotificationContext';
import {
  LayoutDashboard,
  CalendarDays,
  GraduationCap,
  UtensilsCrossed,
  ShoppingCart,
  ListChecks,
  Stethoscope,
  Home,
  Users,
  Brain,
  RotateCcw,
  Bell,
  LogOut,
  Menu,
  X,
  ChevronRight,
  MessageCircle,
  Shield,
  Heart,
  PieChart
} from 'lucide-react';
import { RegulationModeButton, RegulationModeModal } from '../RegulationMode';
import { Button } from '../ui/button';
import { Avatar, AvatarFallback } from '../ui/avatar';
import { Badge } from '../ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { ScrollArea } from '../ui/scroll-area';

// Full navigation items for parent/partner
const fullNavItems = [
  { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { path: '/wellness', label: 'Wellness', icon: Heart },
  { path: '/load-map', label: 'Load Map', icon: PieChart },
  { path: '/calendar', label: 'Calendar', icon: CalendarDays },
  { path: '/school', label: 'School Tracker', icon: GraduationCap },
  { path: '/meals', label: 'Meal Planner', icon: UtensilsCrossed },
  { path: '/groceries', label: 'Grocery List', icon: ShoppingCart },
  { path: '/routines', label: 'Routines', icon: ListChecks },
  { path: '/appointments', label: 'Appointments', icon: Stethoscope },
  { path: '/tasks', label: 'Household Tasks', icon: Home },
  { path: '/handoff', label: 'Handoff Board', icon: Users },
  { path: '/brain-dump', label: 'Brain Dump', icon: Brain },
  { path: '/weekly-reset', label: 'Weekly Reset', icon: RotateCcw },
  { path: '/whatsapp', label: 'WhatsApp Connect', icon: MessageCircle },
  { path: '/family-roles', label: 'Family Roles', icon: Shield },
];

// Asisten-only navigation (simplified view)
const asistenNavItems = [
  { path: '/school', label: "Today's Tasks", icon: GraduationCap },
  { path: '/handoff', label: 'Handoff Board', icon: Users },
];

// Grandparent/Inlaw navigation (readonly)
const readonlyNavItems = [
  { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { path: '/school', label: 'School Tasks', icon: GraduationCap },
];

// Driver navigation
const driverNavItems = [
  { path: '/school', label: 'Pickup/Dropoff', icon: GraduationCap },
];

const getNavItemsForRole = (familyRole) => {
  switch (familyRole) {
    case 'asisten':
      return asistenNavItems;
    case 'grandparent':
    case 'inlaw':
      return readonlyNavItems;
    case 'driver':
      return driverNavItems;
    default:
      return fullNavItems;
  }
};

const AppLayout = () => {
  const { user, family, logout } = useAuth();
  const { notifications, unreadCount, markAsRead, clearAll } = useNotifications();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [regulationModalOpen, setRegulationModalOpen] = useState(false);
  const location = useLocation();

  // Get navigation items based on user's family role
  const familyRole = user?.family_role || 'parent';
  const navItems = getNavItemsForRole(familyRole);
  const isRestrictedRole = ['asisten', 'grandparent', 'inlaw', 'driver'].includes(familyRole);

  const getInitials = (name) => {
    return name?.split(' ').map(n => n[0]).join('').toUpperCase() || 'U';
  };

  const getRoleBadgeColor = (role) => {
    const colors = {
      parent: 'bg-nurture-navy',
      partner: 'bg-teal-500',
      asisten: 'bg-amber-500',
      grandparent: 'bg-nurture-sage',
      driver: 'bg-gray-500',
      inlaw: 'bg-purple-400',
    };
    return colors[role] || 'bg-gray-400';
  };

  return (
    <div className="app-layout bg-nurture-cream min-h-screen">
      {/* Mobile sidebar overlay */}
      <AnimatePresence>
        {sidebarOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/20 z-40 lg:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        )}
      </AnimatePresence>

      {/* Sidebar */}
      <aside className={`
        fixed lg:static inset-y-0 left-0 z-50
        w-72 bg-white shadow-nurture
        transform transition-transform duration-300 ease-in-out
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
      `}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="p-6 border-b border-nurture-cream-dark">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-heading font-bold text-nurture-terracotta">
                  NurtureOS
                </h1>
                <p className="text-sm text-nurture-muted mt-1">
                  {family?.name || 'Family Hub'}
                </p>
              </div>
              <Button
                variant="ghost"
                size="icon"
                className="lg:hidden"
                onClick={() => setSidebarOpen(false)}
                data-testid="close-sidebar-btn"
              >
                <X className="w-5 h-5" />
              </Button>
            </div>
          </div>

          {/* Navigation */}
          <ScrollArea className="flex-1 py-4">
            <nav className="px-3 space-y-1">
              {navItems.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.path;
                
                return (
                  <NavLink
                    key={item.path}
                    to={item.path}
                    onClick={() => setSidebarOpen(false)}
                    data-testid={`nav-${item.path.slice(1)}`}
                    className={`
                      flex items-center gap-3 px-4 py-3 rounded-2xl
                      transition-all duration-200 group
                      ${isActive 
                        ? 'bg-nurture-terracotta text-white' 
                        : 'text-nurture-navy/70 hover:bg-nurture-cream-dark hover:text-nurture-navy'
                      }
                    `}
                  >
                    <Icon className={`w-5 h-5 ${isActive ? '' : 'group-hover:scale-110 transition-transform'}`} />
                    <span className="font-medium">{item.label}</span>
                    {isActive && (
                      <ChevronRight className="w-4 h-4 ml-auto" />
                    )}
                  </NavLink>
                );
              })}
            </nav>
          </ScrollArea>

          {/* Regulation Mode Button - Always visible */}
          <div className="px-3 py-2 border-t border-nurture-cream-dark">
            <RegulationModeButton onClick={() => setRegulationModalOpen(true)} />
          </div>

          {/* User section */}
          <div className="p-4 border-t border-nurture-cream-dark">
            <div className="flex items-center gap-3">
              <Avatar className="w-10 h-10 bg-nurture-sage">
                <AvatarFallback className={`${getRoleBadgeColor(familyRole)} text-white font-bold`}>
                  {getInitials(user?.name)}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <p className="font-medium text-nurture-navy truncate">{user?.name}</p>
                  {isRestrictedRole && (
                    <Badge className={`${getRoleBadgeColor(familyRole)} text-white text-xs`}>
                      {familyRole === 'asisten' ? 'Asisten' : familyRole.charAt(0).toUpperCase() + familyRole.slice(1)}
                    </Badge>
                  )}
                </div>
                <p className="text-xs text-nurture-muted truncate">{user?.email}</p>
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={logout}
                className="text-nurture-muted hover:text-red-500"
                data-testid="logout-btn"
              >
                <LogOut className="w-5 h-5" />
              </Button>
            </div>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <main className="flex-1 min-w-0">
        {/* Top header */}
        <header className="sticky top-0 z-30 bg-nurture-cream/80 backdrop-blur-md border-b border-nurture-cream-dark">
          <div className="flex items-center justify-between px-4 lg:px-8 py-4">
            <Button
              variant="ghost"
              size="icon"
              className="lg:hidden"
              onClick={() => setSidebarOpen(true)}
              data-testid="open-sidebar-btn"
            >
              <Menu className="w-6 h-6" />
            </Button>

            <div className="flex-1 lg:hidden" />

            <div className="flex items-center gap-3">
              {/* Notifications */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    className="relative"
                    data-testid="notifications-btn"
                  >
                    <Bell className="w-5 h-5 text-nurture-navy" />
                    {unreadCount > 0 && (
                      <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center font-bold">
                        {unreadCount > 9 ? '9+' : unreadCount}
                      </span>
                    )}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-80">
                  <div className="flex items-center justify-between px-4 py-2 border-b">
                    <span className="font-semibold text-nurture-navy">Notifications</span>
                    {notifications.length > 0 && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={clearAll}
                        className="text-xs text-nurture-muted"
                      >
                        Clear all
                      </Button>
                    )}
                  </div>
                  <ScrollArea className="max-h-80">
                    {notifications.length === 0 ? (
                      <div className="py-8 text-center text-nurture-muted">
                        <Bell className="w-8 h-8 mx-auto mb-2 opacity-30" />
                        <p>No notifications</p>
                      </div>
                    ) : (
                      notifications.map((notif) => (
                        <DropdownMenuItem
                          key={notif.id}
                          className="flex flex-col items-start gap-1 p-4 cursor-pointer"
                          onClick={() => markAsRead(notif.id)}
                        >
                          <div className="flex items-center gap-2 w-full">
                            <Badge variant={notif.type === 'urgent' ? 'destructive' : 'secondary'}>
                              {notif.type}
                            </Badge>
                            {!notif.is_read && (
                              <span className="w-2 h-2 bg-nurture-terracotta rounded-full ml-auto" />
                            )}
                          </div>
                          <span className="font-medium">{notif.title}</span>
                          <span className="text-sm text-nurture-muted">{notif.message}</span>
                        </DropdownMenuItem>
                      ))
                    )}
                  </ScrollArea>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </header>

        {/* Page content */}
        <div className="p-4 lg:p-8">
          <motion.div
            key={location.pathname}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
          >
            <Outlet />
          </motion.div>
        </div>
      </main>

      {/* Regulation Mode Modal */}
      <RegulationModeModal 
        isOpen={regulationModalOpen} 
        onClose={() => setRegulationModalOpen(false)} 
      />
    </div>
  );
};

export default AppLayout;
